import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Loader2 } from "lucide-react";

type UserRole = "admin" | "installer" | "partner" | null;

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: Array<"admin" | "installer" | "partner">;
  redirectTo?: string;
  fallback?: ReactNode;
}

/**
 * Tarkistaa onko käyttäjällä pääsy sivulle.
 * Admin on superuser ja pääsee kaikkialle.
 */
function hasAccess(
  userRole: UserRole,
  allowedRoles?: Array<"admin" | "installer" | "partner">
): boolean {
  // Admin on superuser - pääsee kaikkialle
  if (userRole === "admin") return true;

  // Jos rooleja ei määritelty, salli kaikki kirjautuneet
  if (!allowedRoles || allowedRoles.length === 0) return true;

  // Tarkista onko käyttäjän rooli sallituissa
  return userRole !== null && allowedRoles.includes(userRole);
}

export function ProtectedRoute({
  children,
  allowedRoles,
  redirectTo = "/auth",
  fallback,
}: ProtectedRouteProps) {
  const { user, role, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (!hasAccess(role, allowedRoles)) {
    // Fallback-sisältö vai uudelleenohjaus
    if (fallback) {
      return <>{fallback}</>;
    }
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
